import { useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTickets } from '@saiens/shared/hooks';
import type { TicketState, TicketPriority, TicketType } from '@saiens/shared/types';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Search, Plus, ChevronRight } from 'lucide-react';

const STATE_LABELS: Record<TicketState, string> = {
  draft: '草稿',
  submitted: '已送出',
  received: '已接收',
  ai_developing: 'AI開發中',
  testing: '已發佈測試',
  user_testing: '使用者確認',
  production: '已上傳正式',
  done: '完成',
};

const STATE_COLORS: Record<TicketState, string> = {
  draft: 'bg-gray-100 text-gray-700',
  submitted: 'bg-blue-100 text-blue-700',
  received: 'bg-yellow-100 text-yellow-700',
  ai_developing: 'bg-purple-100 text-purple-700',
  testing: 'bg-orange-100 text-orange-700',
  user_testing: 'bg-pink-100 text-pink-700',
  production: 'bg-teal-100 text-teal-700',
  done: 'bg-green-100 text-green-700',
};

const PRIORITY_LABELS: Record<TicketPriority, string> = {
  low: '低',
  medium: '中',
  high: '高',
  urgent: '緊急',
};

const PRIORITY_COLORS: Record<TicketPriority, string> = {
  low: 'bg-green-100 text-green-700',
  medium: 'bg-blue-100 text-blue-700',
  high: 'bg-orange-100 text-orange-700',
  urgent: 'bg-red-100 text-red-700',
};

const STATE_FILTERS: { value: string; label: string }[] = [
  { value: '', label: '全部' },
  { value: 'draft', label: '草稿' },
  { value: 'submitted', label: '已送出' },
  { value: 'received', label: '已接收' },
  { value: 'ai_developing', label: '開發中' },
  { value: 'testing', label: '測試中' },
  { value: 'done', label: '完成' },
];

export function TicketListPage() {
  const [searchParams] = useSearchParams();
  const typeFromUrl = (searchParams.get('type') as TicketType) || 'requirement';

  const [activeTab, setActiveTab] = useState<TicketType>(typeFromUrl);
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [stateFilter, setStateFilter] = useState('');

  const { data, isLoading, error } = useTickets({
    type: activeTab,
    search: search || undefined,
    showAll,
    state: stateFilter || undefined,
    limit: 50,
  });

  const handleTabChange = useCallback((tab: TicketType) => {
    setActiveTab(tab);
    setStateFilter('');
  }, []);

  const title = activeTab === 'requirement' ? '系統需求單' : 'BUG處理單';

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white px-4 pt-4 pb-2 shadow-sm">
        <div className="mb-3 flex items-center justify-between">
          <h1 className="text-lg font-bold text-gray-900">{title}</h1>
          <Link
            to={`/ticket/create?type=${activeTab}`}
            className="flex items-center gap-1 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-white"
          >
            <Plus className="h-4 w-4" />
            新增
          </Link>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 rounded-lg bg-gray-100 p-1">
          <button
            type="button"
            onClick={() => handleTabChange('requirement')}
            className={`flex-1 rounded-md py-2 text-sm font-medium transition-colors ${
              activeTab === 'requirement'
                ? 'bg-white text-indigo-600 shadow-sm'
                : 'text-gray-600'
            }`}
          >
            系統需求
          </button>
          <button
            type="button"
            onClick={() => handleTabChange('bug')}
            className={`flex-1 rounded-md py-2 text-sm font-medium transition-colors ${
              activeTab === 'bug'
                ? 'bg-white text-red-600 shadow-sm'
                : 'text-gray-600'
            }`}
          >
            BUG回報
          </button>
        </div>

        {/* Search */}
        <div className="relative mt-3">
          <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="搜尋工單..."
            className="w-full rounded-lg border border-gray-200 py-2 pr-4 pl-10 text-sm focus:border-primary focus:ring-1 focus:ring-primary focus:outline-none"
          />
        </div>

        {/* Filters */}
        <div className="mt-3 flex items-center gap-2 overflow-x-auto pb-1">
          <FilterChip
            label={showAll ? '全部' : '我的'}
            active={false}
            onClick={() => setShowAll((prev) => !prev)}
          />
          <div className="h-4 w-px bg-gray-300" />
          {STATE_FILTERS.map((f) => (
            <FilterChip
              key={f.value}
              label={f.label}
              active={stateFilter === f.value}
              onClick={() => setStateFilter(f.value)}
            />
          ))}
        </div>
      </div>

      {/* List */}
      <div className="flex-1 p-4">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : error ? (
          <div className="py-12 text-center text-red-500">載入失敗</div>
        ) : !data || data.items.length === 0 ? (
          <div className="py-12 text-center text-gray-500">
            {search ? '找不到符合條件的工單' : '尚無工單'}
          </div>
        ) : (
          <div className="space-y-3">
            {data.items.map((ticket) => (
              <Link
                key={ticket.id}
                to={`/ticket/${ticket.id}`}
                className="block rounded-xl bg-white p-4 shadow-sm transition-shadow hover:shadow-md"
              >
                <div className="flex items-start justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-gray-900">
                        {ticket.name}
                      </span>
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                          PRIORITY_COLORS[ticket.priority]
                        }`}
                      >
                        {PRIORITY_LABELS[ticket.priority]}
                      </span>
                    </div>
                    <div className="mt-1 flex items-center gap-2 text-xs text-gray-500">
                      {ticket.system_name && (
                        <span>{ticket.system_name}</span>
                      )}
                      {ticket.system_name && ticket.function_name && (
                        <span>/</span>
                      )}
                      {ticket.function_name && (
                        <span>{ticket.function_name}</span>
                      )}
                    </div>
                    <div className="mt-2 flex items-center gap-3">
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                          STATE_COLORS[ticket.state]
                        }`}
                      >
                        {STATE_LABELS[ticket.state]}
                      </span>
                      {ticket.completion > 0 && (
                        <div className="flex items-center gap-1">
                          <div className="h-1.5 w-16 rounded-full bg-gray-200">
                            <div
                              className="h-1.5 rounded-full bg-primary"
                              style={{ width: `${ticket.completion}%` }}
                            />
                          </div>
                          <span className="text-xs text-gray-500">
                            {ticket.completion}%
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 shrink-0 text-gray-400" />
                </div>
                {ticket.employee_name && (
                  <div className="mt-2 text-xs text-gray-400">
                    提報人：{ticket.employee_name}
                    {ticket.create_date && (
                      <span className="ml-2">
                        {new Date(ticket.create_date).toLocaleDateString('zh-TW')}
                      </span>
                    )}
                  </div>
                )}
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`shrink-0 rounded-full px-3 py-1 text-xs font-medium transition-colors ${
        active
          ? 'bg-primary text-white'
          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
      }`}
    >
      {label}
    </button>
  );
}
